import React from 'react';
import { Row, Col, FormGroup, Button } from 'reactstrap';
import { mapValues } from 'lodash-es';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import * as moment from 'moment';
import cls from 'classnames';
import CommonContext, { MomentDateFormat, ApiRoutes, ChargeTypeResourceTypes, ChargeTypeUnits } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { util } from '../Util';
import {
    FormLabel, FormCheckbox, FlexEndRow,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { BillingRateCountyGroup } from './BillingRateCountyGroup';
import './BillingRateCountyGroup.scss';
import Input from 'components/common/forms/Input';
import NumericInput from 'components/common/forms/NumericInput';

export default class BillingRateForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: true,
            errors: {},
            formValidated: false,
            validationMessage: '',
            isValidated: false,
            inventoryItems: [],
            rentalUnitTypes: [],
            associateInventoryItem: false,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onRemove = (e) => {
        this.context.setFormOpened(false);
        this.props.onRemoveCallback();
    };

    onSubmit = (e) => {
        this.context.setFormOpened(false);
        this.props.onSaveCallback();
    };

    async populateState() {
        const [inventoryItemsRaw, rentalUnitTypes] = await Promise.all([
            util.fetch.js(ApiRoutes.typeAheads.quickBooksInventoryItems()),
            util.fetch.js(ApiRoutes.typeAheads.rentalUnitTypes()),
        ]);

        let inventoryItems = [];

        if ((inventoryItemsRaw ?? []).length) {
            inventoryItems = inventoryItemsRaw.map((x) => ({ label: x.name, value: x.id }));
        }

        this.setState((state) => ({
            loading: false,
            inventoryItems,
            rentalUnitTypes,
            associateInventoryItem: this.context.tenant.tenantSettings.useQuickBooksInvoicing,
        }));
    }

    resetForm = () => this.setState({ formValidated: false, errors: {} });

    getEntityName = () => this.props.entityName ? `${this.props.entityName}.` : '';

    excludeEmptyRates = () => {
        const { entity, onChange } = this.props;
        
        const countyGroups = mapValues(entity.usCountyGroupBillingRates || {}, (rate, index) => {
            if (!rate.billingRate.rate) {
                return {
                    ...rate,
                    billingRate: {
                        ...rate.billingRate,
                        current: false,
                    },
                };
            }

            return rate;
        });
        // `${entityName}usCountyGroupBillingRates.${index}.billingRate.current`
        onChange({
            target: {
                name: `${this.getEntityName()}usCountyGroupBillingRates`,
                value: countyGroups,
            },
        });
    };
 
    render() {
        const { tenantSettings } = (this.context ?? {}).tenant ?? {};
        if (!tenantSettings.billingRatesEnabled) return <NotAuthorizedPage />;

        if (!this.props.entity) return '';
        const { chargeTypeReadonly, entity } = this.props;

        const name = this.getEntityName();

        const chargeTypes = this.props.chargeTypes ?? [];
        const inventoryItems = this.state.inventoryItems ?? [];
        const rentalUnitTypes = this.state.rentalUnitTypes ?? [];

        const countyGroups = Object.values(entity.usCountyGroupBillingRates || {});
        const hasCountyGroups = countyGroups.length > 0;

        const selectedChargeType = chargeTypes?.find((i) => (i.value ?? i.id) === entity.chargeTypeId);

        const doubleTimeEnabled = 
            !!this.props.showDoubleTime
            && selectedChargeType?.resourceTypeId === ChargeTypeResourceTypes.Employee
            && selectedChargeType?.unitsId === ChargeTypeUnits.Hours
            && !!tenantSettings.enableDoubleTimeBilling;

        const isRentalChargeType = selectedChargeType?.resourceTypeId === ChargeTypeResourceTypes.Rental;
        const descriptionWidth = isRentalChargeType ? 3 : 6;

        return (
            <SlideForm
                size="col-xl-9 col-md-9 col-xs-9"
                loading={this.state.loading}
                show={this.props.show}
                id="billingRateForm"
                formIcon={faMinusCircle}
                formTitle={
                    !!parseInt(this.props.entity.id ?? 0) > 0
                        ? 'Edit Rate'
                        : 'Add Rate'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={this.state.formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                validationMessage={this.state.validationMessage}
                showDelete={this.props.showRemove === true}
                onDelete={this.onRemove}
            >
                <>
                    {!!this.props.showChargeType == true && (
                        <>
                            <FormGroup>
                                <FormLabel
                                    className="ml-3"
                                    htmlFor="chargeTypesDropdown"
                                    text="Charge"
                                    required={!entity.chargeTypeId}
                                />
                                {!chargeTypeReadonly && (
                                    <ValidatedSelect
                                        className="ml-3 w-auto"
                                        isClearable={false}
                                        id={`${name}chargeTypeId`}
                                        name={`${name}chargeTypeId`}
                                        required
                                        value={
                                            chargeTypes.find(
                                                (i) => i.value
                                                    == entity.chargeTypeId,
                                            ) ?? ''
                                        }
                                        options={chargeTypes ?? []}
                                        onChange={this.props.onSelectChange}
                                        readOnly
                                        getOptionLabel={(o) => o.label}
                                        getOptionValue={(o) => o.value}
                                        validationMessage="A charge is required."
                                    />
                                )}
                                {chargeTypeReadonly && (
                                    <Input
                                        formGroupClassName="ml-3 w-auto mb-0"
                                        readOnly
                                        value={entity.chargeTypeName}
                                    />
                                )}
                            </FormGroup>

                            {!!(entity.chargeTypeId ?? {}) && !hasCountyGroups && (
                                <>
                                    <Input
                                        label="Minimum Bill Hours"
                                        formGroupClassName="ml-3 w-auto"
                                        readOnly
                                        value={
                                            (
                                                chargeTypes.find(
                                                    (i) => i.value
                                                        == entity.chargeTypeId,
                                                ) ?? {}
                                            ).minimumBillUnits
                                                ? (
                                                    chargeTypes.find(
                                                        (i) => i.value
                                                                == entity.chargeTypeId,
                                                    ) ?? {}
                                                ).minimumBillUnits.toFixed(
                                                    2,
                                                )
                                                : 'N/A'
                                        }
                                        placeholder="Min Bill Hours"
                                    />
                                    <Input
                                        label="Maximum Bill Hours"
                                        formGroupClassName="ml-3 w-auto"
                                        readOnly
                                        value={
                                            (
                                                chargeTypes.find(
                                                    (i) => i.value
                                                        == entity.chargeTypeId,
                                                ) ?? {}
                                            ).maximumBillUnits
                                                ? (
                                                    chargeTypes.find(
                                                        (i) => i.value
                                                                == entity.chargeTypeId,
                                                    ) ?? {}
                                                ).maximumBillUnits.toFixed(
                                                    2,
                                                )
                                                : 'N/A'
                                        }
                                        placeholder="Max Bill Hours"
                                    />
                                
                                </>
                            )}
                        </>
                    )}

                    {Boolean(hasCountyGroups) && (
                        <Row className="g-0 mb-1">
                            <Col xs="12" className="ml-3">
                                <strong className="text-muted">Regular Rate</strong>
                            </Col>
                        </Row>
                    )}

                    <Row className={cls('g-0', hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                        {!hasCountyGroups && (
                            <Col xs="3">
                                <NumericInput
                                    label="Rate"
                                    formGroupClassName="ml-3 w-auto"
                                    id={`${name}billingRate.rate`}
                                    name={`${name}billingRate.rate`}
                                    value={entity.billingRate.rate ?? {}}
                                    onChange={this.props.onChange}
                                    placeholder="Enter Rate"
                                    step="0.01"
                                    required
                                    error="Rate is required."
                                />
                            </Col>
                        )}

                        <Col xs={this.state.associateInventoryItem ? descriptionWidth : '9'}>
                            <Input
                                label="Description"
                                formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                id={`${name}billingRate.billingRaterateDescription`}
                                name={`${name}billingRate.rateDescription`}
                                value={
                                    entity.billingRate.rateDescription ?? ''
                                }
                                onChange={this.props.onChange}
                                placeholder="Rate Description"
                                maxLength="100"
                            />
                        </Col>

                        {Boolean(isRentalChargeType) && (
                            <Col xs={3}>
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="rentalUnitTypeId"
                                        text="Rental Unit"
                                        required
                                    />
                                    <ValidatedSelect
                                        className="w-auto"
                                        id="rentalUnitTypeId"
                                        name={`${name}billingRate.rentalUnitTypeId`}
                                        options={rentalUnitTypes}
                                        required
                                        value={
                                            (rentalUnitTypes ?? []).find(
                                                (s) => s.value
                                                        == entity.billingRate
                                                            .rentalUnitTypeId,
                                            ) ?? ''
                                        }
                                        onChange={this.props.onSelectChange}
                                    />
                                </FormGroup>
                            </Col>
                        )}

                        {/* If the tenant setting exists and there are inventory items to load */}
                        {this.state.associateInventoryItem
                            && inventoryItems.length > 0 && (
                            <Col xs="3">
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="rateQBInventoryItemId"
                                        text="Inventory Item"
                                        required={false}
                                    />
                                    <ValidatedSelect
                                        className="w-auto"
                                        id="rateQBInventoryItemId"
                                        name={`${name}billingRate.rateQBInventoryItemId`}
                                        options={inventoryItems}
                                        value={
                                            (inventoryItems ?? []).find(
                                                (s) => s.value
                                                        == entity.billingRate
                                                            .rateQBInventoryItemId,
                                            ) ?? ''
                                        }
                                        onChange={this.props.onSelectChange}
                                    />
                                </FormGroup>
                            </Col>
                        )}
                    </Row>

                    {Boolean(hasCountyGroups) && !isRentalChargeType && (
                        <Row className="g-0 mb-1">
                            <Col xs="12" className="ml-3">
                                <strong className="text-muted">Overtime Rate</strong>
                            </Col>
                        </Row>
                    )}

                    {!isRentalChargeType && (
                        <>
                            <Row className={cls('g-0', hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                                {!hasCountyGroups && (
                                    <Col xs="3">
                                        <NumericInput
                                            label="Overtime Rate"
                                            formGroupClassName="w-auto"
                                            id={`${name}billingRate.otRate`}
                                            name={`${name}billingRate.otRate`}
                                            value={entity.billingRate.otRate ?? {}}
                                            onChange={this.props.onChange}
                                            placeholder="Enter Overtime Rate"
                                            step="0.01"
                                        />
                                    </Col>
                                )}

                                <Col xs={this.state.associateInventoryItem ? '6' : '9'}>
                                    <Input
                                        label="Description"
                                        formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                        id={`${name}billingRate.otRateDescription`}
                                        name={`${name}billingRate.otRateDescription`}
                                        value={
                                            entity.billingRate.otRateDescription ?? ''
                                        }
                                        onChange={this.props.onChange}
                                        placeholder="Rate Description"
                                        maxLength="100"
                                    />
                                </Col>
                                {/* If the tenant setting exists and there are inventory items to load */}
                                {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                    <Col xs="3">
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="otRateQBInventoryItemId"
                                                text="Inventory Item"
                                                required={false}
                                            />
                                            <ValidatedSelect
                                                className="w-auto"
                                                id="otRateQBInventoryItemId"
                                                name={`${name}billingRate.otRateQBInventoryItemId`}
                                                options={inventoryItems}
                                                value={
                                                    (inventoryItems ?? []).find(
                                                        (s) => s.value
                                                                == entity.billingRate
                                                                    .otRateQBInventoryItemId,
                                                    ) ?? ''
                                                }
                                                onChange={this.props.onSelectChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>

                            {Boolean(this.props.showDoubleTime) && (
                                <>
                                    {Boolean(hasCountyGroups) && (
                                        <Row className="g-0 mb-1">
                                            <Col xs="12" className="ml-3">
                                                <strong className="text-muted">Double Rate</strong>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className={cls('g-0', hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                                        {!hasCountyGroups && (
                                            <Col xs="3">
                                                <NumericInput
                                                    label="Double-Time Rate"
                                                    formGroupClassName="w-auto"
                                                    id={`${name}billingRate.altRate`}
                                                    name={`${name}billingRate.altRate`}
                                                    value={entity.billingRate.altRate ?? {}}
                                                    onChange={this.props.onChange}
                                                    placeholder="Enter DT Rate"
                                                    step="0.01"
                                                    disabled={!doubleTimeEnabled}
                                                />
                                            </Col>
                                        )}
                                
                                        <Col xs={this.state.associateInventoryItem ? '6' : '9'}>
                                            <Input
                                                label="Description"
                                                formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                                id={`${name}billingRate.altRateDescription`}
                                                name={`${name}billingRate.altRateDescription`}
                                                value={entity.billingRate.altRateDescription ?? ''}
                                                onChange={this.props.onChange}
                                                placeholder="Rate Description"
                                                maxLength="100"
                                                disabled={!doubleTimeEnabled}
                                            />
                                        </Col>
                                        {/* If the tenant setting exists and there are inventory items to load */}
                                        {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                            <Col xs="3">
                                                <FormGroup>
                                                    <FormLabel
                                                        htmlFor="altRateQBInventoryItemId"
                                                        text="Inventory Item"
                                                        required={false}
                                                    />
                                                    <ValidatedSelect
                                                        className="w-auto"
                                                        id="altRateQBInventoryItemId"
                                                        name={`${name}billingRate.altRateQBInventoryItemId`}
                                                        disabled={!doubleTimeEnabled}
                                                        options={inventoryItems}
                                                        value={
                                                            (inventoryItems ?? []).find(
                                                                (s) => s.value
                                                                == entity.billingRate
                                                                    .altRateQBInventoryItemId,
                                                            ) ?? ''
                                                        }
                                                        onChange={this.props.onSelectChange}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>
                                </>
                            )}
                        </>
                    )}

                    <Input
                        label="Effective Date"
                        formGroupClassName="ml-3 w-auto"
                        id={`${name}billingRate.effectiveDate`}
                        name={`${name}billingRate.effectiveDate`}
                        onChange={this.props.onChange}
                        value={
                            moment(entity.billingRate.effectiveDate).format(
                                MomentDateFormat.DateOnlyUTCHyphenated,
                            ) ?? ''
                        }
                        type="date"
                        required
                        error="Effective Date is required."
                    />
                    <Input
                        label="Expiration Date"
                        formGroupClassName="ml-3 w-auto"
                        id={`${name}billingRate.expirationDate`}
                        name={`${name}billingRate.expirationDate`}
                        value={
                            moment(
                                entity.billingRate.expirationDate,
                            ).format(
                                MomentDateFormat.DateOnlyUTCHyphenated,
                            ) ?? ''
                        }
                        onChange={this.props.onChange}
                        type="date"
                    />

                    {!countyGroups?.length && (
                        <FormGroup>
                            <FormLabel
                                className="ml-3"
                                text="Active"
                                required={false}
                            />
                            <FormCheckbox
                                className="ml-3 w-auto"
                                id={`${name}billingRate.current`}
                                name={`${name}billingRate.current`}
                                checked={entity.billingRate.current ?? ''}
                                onChange={this.props.onChange}
                                labelText="Active"
                            />
                        </FormGroup>
                    )}

                    {countyGroups.length > 0 && (
                        <>
                            <table className="table table-sm countyGroupTimesheetTable borderless">
                                <thead>
                                    <tr>
                                        <th className="badge-column" />
                                        <th>Group</th>
                                        <th className="not-applicable">
                                            Exclude
                                        </th>
                                        <th className="numeric-input">
                                            REG
                                        </th>

                                        <th className="numeric-input">
                                            OT
                                        </th>

                                        {Boolean(this.props.showDoubleTime) && (
                                            <th className="numeric-input">
                                                DOUBLE
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {countyGroups.map((rate, index) => (
                                        <BillingRateCountyGroup
                                            key={rate.usCountyGroup.id}
                                            entityName={name}
                                            onChange={this.props.onChange}
                                            index={index}
                                            doubleTimeEnabled={doubleTimeEnabled}
                                            showDoubleTime={this.props.showDoubleTime}
                                            {...rate.usCountyGroup}
                                            {...rate.billingRate}
                                        />
                                    ))}
                                </tbody>
                            </table>

                            <FlexEndRow className="pt-1 pl-3 pr-4">
                                <Button
                                    color="link"
                                    size="sm"
                                    className="site-link w-100 text-right"
                                    role="button"
                                    onClick={this.excludeEmptyRates}
                                >
                                    Exclude All Empty Rates
                                </Button>
                            </FlexEndRow>
                        </>
                    )}
                </>
            </SlideForm>
        );
    }
}
